import { useCallback, useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { ZIGBOT_ATTENTION_LOOP_MS, ZIGBOT_ATTENTION_PLAY_COUNT } from '@/components/ZigBot/zigBotIconAssets';
import {
    isHubIconAnimating,
    msUntilIconAnimationAllowed,
    subscribeToIconAnimations,
} from '@/components/FloatingActionBar/iconAnimationCoordinator';

const IDLE_DELAY_MS = 10 * 60 * 1000;

/** A first-timer's nudge waits for the page to settle rather than firing on the first frame. */
const FIRST_TIME_DELAY_MS = 3000;

// Backstop for a failed fetch or a renderer that never emits `complete`. Generous, since it also
// covers however long the JSON takes to arrive.
const ATTENTION_TIMEOUT_MS = ZIGBOT_ATTENTION_LOOP_MS * ZIGBOT_ATTENTION_PLAY_COUNT + 6000;

/** Exported for the tests so they assert the behavior rather than restate the numbers. */
export const ZIGBOT_ATTENTION_TIMING = {
    idleDelayMs: IDLE_DELAY_MS,
    firstTimeDelayMs: FIRST_TIME_DELAY_MS,
    timeoutMs: ATTENTION_TIMEOUT_MS,
} as const;

const SEEN_KEY = (userId: string) => `zigbot-attention-seen-v1-${userId}`;

const hasSeenAttention = (userId: string): boolean => {
    try {
        return localStorage.getItem(SEEN_KEY(userId)) === 'true';
    }
    catch {
        return false;
    }
};

const markAttentionSeen = (userId: string): void => {
    try {
        localStorage.setItem(SEEN_KEY(userId), 'true');
    }
    catch {
        // Storage blocked so the welcome nudge may show again next session.
    }
};

export interface UseZigBotIdleAttentionResult {
    isAnimating: boolean;
    /** Restarts the idle clock. Call on any interaction with ZigBot. */
    dismiss: () => void;
    onAnimationComplete: () => void;
}

/**
 * Two triggers: once shortly after the icon first appears, then on every idle interval ZigBot goes
 * untouched. Both defer to the Hub icon, and a nudge deferred that way is rescheduled, not dropped.
 */
export const useZigBotIdleAttention = (userId: string, enabled: boolean): UseZigBotIdleAttentionResult => {
    const prefersReducedMotion = usePrefersReducedMotion();
    const [isAnimating, setIsAnimating] = useState(false);

    // A single pending timer, whether it is counting down to a nudge or waiting out the Hub's
    // buffer. Held in a ref so rescheduling never re-runs the effects below.
    const nextPlayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearPending = useCallback(() => {
        if (nextPlayRef.current !== null) {
            clearTimeout(nextPlayRef.current);
            nextPlayRef.current = null;
        }
    }, []);

    /**
     * Plays after `delayMs`, unless the Hub is animating by then, in which case it waits out the
     * buffer and asks again rather than playing over it or giving up on this cycle.
     */
    const playIn = useCallback((delayMs: number) => {
        clearPending();
        nextPlayRef.current = setTimeout(() => {
            nextPlayRef.current = null;
            const wait = msUntilIconAnimationAllowed();
            if (wait > 0) {
                playIn(wait);
                return;
            }
            setIsAnimating(true);
        }, delayMs);
    }, [clearPending]);

    const dismiss = useCallback(() => {
        clearPending();
        setIsAnimating(false);
        if (enabled && !prefersReducedMotion) {
            playIn(IDLE_DELAY_MS);
        }
    }, [clearPending, enabled, playIn, prefersReducedMotion]);

    useEffect(() => {
        // Checked inside the hook so it can't be forgotten by a caller.
        if (!enabled || prefersReducedMotion || !userId) {
            return;
        }
        playIn(hasSeenAttention(userId) ? IDLE_DELAY_MS : FIRST_TIME_DELAY_MS);
        return clearPending;
    }, [enabled, prefersReducedMotion, userId, playIn, clearPending]);

    useEffect(() => {
        if (!isAnimating) {
            return;
        }
        markAttentionSeen(userId);

        const stopTimer = setTimeout(() => {
            setIsAnimating(false);
            playIn(IDLE_DELAY_MS);
        }, ATTENTION_TIMEOUT_MS);

        // The Hub takes precedence even mid-nudge so stand down and come back on the next cycle.
        const unsubscribe = subscribeToIconAnimations(() => {
            if (isHubIconAnimating()) {
                setIsAnimating(false);
                playIn(IDLE_DELAY_MS);
            }
        });

        return () => {
            clearTimeout(stopTimer);
            unsubscribe();
        };
    }, [isAnimating, userId, playIn]);

    const onAnimationComplete = useCallback(() => {
        setIsAnimating(false);
        playIn(IDLE_DELAY_MS);
    }, [playIn]);

    return { isAnimating, dismiss, onAnimationComplete };
};
